import { createReducer, on } from '@ngrx/store';
import { NotificationsDrawerActions } from './notifications-drawer.actions';
import {
  buildInitialExpandedSections,
  buildInitialTypePageStates,
  initialState,
  NotificationsDrawerState,
  PendingDeleteEntry
} from './notifications-drawer.state';

export const notificationsDrawerReducer = createReducer<NotificationsDrawerState>(
  initialState,

  on(NotificationsDrawerActions.loadNotifications, state => ({
    ...state,
    loading: true,
    error: null
  })),

  on(NotificationsDrawerActions.loadNotificationsSuccess, (state, { response }) => ({
    ...state,
    loading: false,
    categories: response.groupedByCategory,
    totalCount: response.totalCount,
    unreadCount: response.unreadCount,
    typePageStates: buildInitialTypePageStates(response.groupedByCategory),
    expandedSections: buildInitialExpandedSections(response.groupedByCategory)
  })),

  on(NotificationsDrawerActions.loadNotificationsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  on(NotificationsDrawerActions.loadMoreForType, (state, { typeCode }) => ({
    ...state,
    typePageStates: {
      ...state.typePageStates,
      [typeCode]: {
        ...state.typePageStates[typeCode],
        loadingMore: true
      }
    }
  })),

  on(NotificationsDrawerActions.loadMoreForTypeSuccess, (state, { typeCode, response, appendCount }) => {
    // Only keep appendCount items when refilling (e.g. 1 after a delete);
    // keep all items for a normal user-initiated Show More.
    const newItems = appendCount != null ? response.notifications.slice(0, appendCount) : response.notifications;

    return {
      ...state,
      categories: state.categories.map(cat => ({
        ...cat,
        notificationsByType: cat.notificationsByType.map(nt => {
          if (nt.notificationTypeCode !== typeCode) return nt;

          const existingIds = new Set(nt.notifications.map(n => n.notificationId));
          const dedupedItems = newItems.filter(n => !existingIds.has(n.notificationId));
          return {
            ...nt,
            notifications: [...nt.notifications, ...dedupedItems],
            totalCount: response.totalCount
            // unreadCount is NOT overwritten — it reflects the lifetime total
            // for this type and is already maintained by markAsReadSuccess /
            // deleteNotificationSuccess.
          };
        })
      })),
      typePageStates: {
        ...state.typePageStates,
        [typeCode]: {
          ...state.typePageStates[typeCode],
          loadingMore: false,
          needsRefill: false
        }
      }
    };
  }),

  on(NotificationsDrawerActions.loadMoreForTypeFailure, (state, { typeCode }) => ({
    ...state,
    typePageStates: {
      ...state.typePageStates,
      [typeCode]: {
        ...state.typePageStates[typeCode],
        loadingMore: false
      }
    }
  })),

  on(NotificationsDrawerActions.markAsReadSuccess, (state, { notificationId }) => ({
    ...state,
    unreadCount: state.unreadCount > 0 ? state.unreadCount - 1 : 0,
    categories: state.categories.map(cat => {
      const hadUnread = cat.notificationsByType.some(nt =>
        nt.notifications.some(n => n.notificationId === notificationId && !n.read)
      );
      return {
        ...cat,
        unreadCount: hadUnread && cat.unreadCount > 0 ? cat.unreadCount - 1 : cat.unreadCount,
        notificationsByType: cat.notificationsByType.map(nt => {
          const notification = nt.notifications.find(n => n.notificationId === notificationId);
          if (!notification || notification.read) return nt;
          return {
            ...nt,
            unreadCount: nt.unreadCount > 0 ? nt.unreadCount - 1 : 0,
            notifications: nt.notifications.map(n => (n.notificationId === notificationId ? { ...n, read: true } : n))
          };
        })
      };
    })
  })),

  on(NotificationsDrawerActions.toggleSectionExpansion, (state, { typeCode }) => ({
    ...state,
    expandedSections: {
      ...state.expandedSections,
      [typeCode]: !state.expandedSections[typeCode]
    }
  })),

  on(NotificationsDrawerActions.resetNotificationsDrawer, state => ({
    ...state,
    expandedSections: {}
  })),

  on(NotificationsDrawerActions.softDeleteNotification, (state, { notificationId, typeCode }) => {
    let deletedEntry: PendingDeleteEntry | undefined;

    const updatedCategories = state.categories.map(cat => ({
      ...cat,
      notificationsByType: cat.notificationsByType.map(nt => {
        if (nt.notificationTypeCode !== typeCode) return nt;
        const idx = nt.notifications.findIndex(n => n.notificationId === notificationId);
        if (idx === -1) return nt;
        deletedEntry = {
          notification: nt.notifications[idx],
          typeCode,
          wasUnread: !nt.notifications[idx].read,
          insertionIndex: idx
        };
        return {
          ...nt,
          notifications: nt.notifications.filter(n => n.notificationId !== notificationId)
        };
      })
    }));

    if (!deletedEntry) return state;

    return {
      ...state,
      categories: updatedCategories,
      pendingDeletes: {
        ...state.pendingDeletes,
        [notificationId]: deletedEntry
      }
    };
  }),

  on(NotificationsDrawerActions.deleteNotification, (state, { notificationId }) => ({
    ...state,
    deletingNotificationIds: [...state.deletingNotificationIds, notificationId]
  })),

  on(NotificationsDrawerActions.deleteNotificationFailure, (state, { notificationId }) => ({
    ...state,
    deletingNotificationIds: state.deletingNotificationIds.filter(id => id !== notificationId)
  })),

  on(NotificationsDrawerActions.undoDelete, (state, { notificationId }) => {
    const entry = state.pendingDeletes[notificationId];
    if (!entry) return state;

    const { [notificationId]: _removed, ...remainingPendingDeletes } = state.pendingDeletes;

    return {
      ...state,
      categories: state.categories.map(cat => ({
        ...cat,
        notificationsByType: cat.notificationsByType.map(nt => {
          if (nt.notificationTypeCode !== entry.typeCode) return nt;
          const notifications = [...nt.notifications];
          notifications.splice(entry.insertionIndex, 0, entry.notification);
          return { ...nt, notifications };
        })
      })),
      pendingDeletes: remainingPendingDeletes
    };
  }),

  // deleteNotificationFailure: restore the notification that was soft-deleted and clean up pendingDeletes.
  // This mirrors undoDelete — the only difference is it's triggered by an API error rather than a user action.
  on(NotificationsDrawerActions.deleteNotificationFailure, (state, { notificationId }) => {
    const entry = state.pendingDeletes[notificationId];
    if (!entry) return state;

    const { [notificationId]: _removedEntry, ...remainingPendingDeletes } = state.pendingDeletes;

    return {
      ...state,
      categories: state.categories.map(cat => ({
        ...cat,
        notificationsByType: cat.notificationsByType.map(nt => {
          if (nt.notificationTypeCode !== entry.typeCode) return nt;
          const notifications = [...nt.notifications];
          notifications.splice(entry.insertionIndex, 0, entry.notification);
          return { ...nt, notifications };
        })
      })),
      pendingDeletes: remainingPendingDeletes
    };
  }),

  on(NotificationsDrawerActions.deleteNotificationSuccess, (state, { notificationId, typeCode, wasUnread }) => {
    const { [notificationId]: _removed, ...remainingPendingDeletes } = state.pendingDeletes;

    // Find categories that contain this notification type and update counts.
    // The notification itself may already have been removed by softDeleteNotification,
    // so we search by typeCode rather than by notification presence.
    const updatedCategories = state.categories.map(cat => {
      const hasType = cat.notificationsByType.some(nt => nt.notificationTypeCode === typeCode);
      if (!hasType) return cat;

      return {
        ...cat,
        totalCount: cat.totalCount > 0 ? cat.totalCount - 1 : 0,
        unreadCount: wasUnread && cat.unreadCount > 0 ? cat.unreadCount - 1 : cat.unreadCount,
        notificationsByType: cat.notificationsByType.map(nt => {
          if (nt.notificationTypeCode !== typeCode) return nt;
          return {
            ...nt,
            totalCount: nt.totalCount > 0 ? nt.totalCount - 1 : 0,
            unreadCount: wasUnread && nt.unreadCount > 0 ? nt.unreadCount - 1 : nt.unreadCount,
            // Remove if still present (direct delete path); no-op if already removed by softDeleteNotification.
            notifications: nt.notifications.filter(n => n.notificationId !== notificationId)
          };
        })
      };
    });

    const updatedTypeEntry = updatedCategories
      .flatMap(cat => cat.notificationsByType)
      .find(nt => nt.notificationTypeCode === typeCode);
    const needsRefill = updatedTypeEntry ? updatedTypeEntry.notifications.length < updatedTypeEntry.totalCount : false;

    return {
      ...state,
      totalCount: state.totalCount > 0 ? state.totalCount - 1 : 0,
      unreadCount: wasUnread && state.unreadCount > 0 ? state.unreadCount - 1 : state.unreadCount,
      categories: updatedCategories,
      deletingNotificationIds: state.deletingNotificationIds.filter(id => id !== notificationId),
      typePageStates: {
        ...state.typePageStates,
        [typeCode]: {
          ...state.typePageStates[typeCode],
          needsRefill
        }
      }
    };
  })
);
